import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Brain,
  Sun,
  Moon,
  Gamepad2,
  ChevronDown,
  Stethoscope,
  GraduationCap,
  ShieldCheck,
  SlidersHorizontal,
} from 'lucide-react';
import { cn } from '../../utils';

interface DashboardHeaderProps {
  isDark: boolean;
  setIsDark: (v: boolean) => void;
  L: number;
  onToggleFilters: () => void;
}

export default function DashboardHeader({
  isDark,
  setIsDark,
  L,
  onToggleFilters,
}: DashboardHeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <header className="border-border-main bg-surface/70 sticky top-0 z-50 border-b backdrop-blur-xl">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <motion.div
            whileHover={{ rotate: 15 }}
            className="from-primary to-primary-dark shadow-primary/20 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br shadow-lg"
          >
            <Brain className="h-7 w-7 text-white" />
          </motion.div>
          <div>
            <h1 className="text-text-main text-xl font-extrabold tracking-tight">
              <span className="text-primary">Neural</span> Quest
            </h1>
            <div className="flex items-center gap-2">
              <span className="bg-accent h-2 w-2 animate-pulse rounded-full" />
              <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase dark:text-slate-400">
                MMEM Framework
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 sm:gap-6">
          <div className="relative">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="hover:text-primary flex items-center gap-2 text-sm font-bold text-slate-500 transition-colors dark:text-slate-400"
            >
              <span className="hidden sm:inline">Apps</span>
              <ChevronDown
                className={cn(
                  'h-4 w-4 transition-transform',
                  isMenuOpen && 'rotate-180'
                )}
              />
            </button>
            <AnimatePresence>
              {isMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-surface border-border-main absolute top-full left-0 z-10 mt-2 w-64 rounded-2xl border p-2 shadow-xl sm:right-0 sm:left-auto"
                  onMouseLeave={() => setIsMenuOpen(false)}
                >
                  <Link
                    to="/cbt-companion"
                    className="group flex items-center gap-3 rounded-lg p-3 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-100 hover:text-indigo-600 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    <Stethoscope className="h-5 w-5 text-indigo-500" />
                    CBT Companion
                  </Link>
                  <Link
                    to="/edu-evaluator"
                    className="group flex items-center gap-3 rounded-lg p-3 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-100 hover:text-emerald-600 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    <GraduationCap className="h-5 w-5 text-emerald-500" />
                    Edu-Evaluator
                  </Link>
                  <Link
                    to="/pro-sim-license"
                    className="group flex items-center gap-3 rounded-lg p-3 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    <ShieldCheck className="h-5 w-5 text-slate-500" />
                    Pro-Sim License
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link
            to="/"
            className="hover:text-primary flex items-center gap-2 text-sm font-bold text-slate-500 transition-colors dark:text-slate-400"
          >
            <Gamepad2 className="h-5 w-5" />
            <span className="hidden sm:inline">Neural Quest</span>
          </Link>
          <button
            onClick={() => setIsDark(!isDark)}
            className="rounded-xl bg-slate-100 p-2.5 text-slate-600 transition-colors hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            aria-label="Toggle theme"
          >
            {isDark ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </button>
          <button
            onClick={onToggleFilters}
            className="rounded-xl bg-slate-100 p-2.5 text-slate-600 transition-colors hover:bg-slate-200 lg:hidden dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            aria-label="Toggle Filters"
          >
            <SlidersHorizontal className="h-5 w-5" />
          </button>
          <div className="hidden flex-col items-end sm:flex">
            <span className="text-[10px] font-bold tracking-tighter text-slate-400 uppercase dark:text-slate-500">
              Current Stability
            </span>
            <motion.span
              key={L}
              initial={{ scale: 1.1, color: '#6366f1' }}
              animate={{ scale: 1, color: isDark ? '#f8fafc' : '#0f172a' }}
              className="font-mono text-2xl font-black tabular-nums"
            >
              {L.toLocaleString()}
            </motion.span>
          </div>
        </div>
      </div>
    </header>
  );
}
